import { useMemo } from 'react';

/**
 * Número de WhatsApp: o código do país é escolhido em uma lista e o número é digitado à parte.
 *
 * Digitar o país junto com o número é onde nasce o erro caro — um dígito trocado no começo manda o
 * código de verificação para outro país. A lista começa pelo Golfo, que é o mercado da plataforma.
 */

export interface Country {
  code: string;   // ISO, usado como chave
  dial: string;   // com o sinal de mais
  flag: string;
  name: string;   // em árabe
  nameEn: string;
}

export const COUNTRIES: Country[] = [
  { code: 'AE', dial: '+971', flag: '🇦🇪', name: 'الإمارات', nameEn: 'United Arab Emirates' },
  { code: 'SA', dial: '+966', flag: '🇸🇦', name: 'السعودية', nameEn: 'Saudi Arabia' },
  { code: 'KW', dial: '+965', flag: '🇰🇼', name: 'الكويت', nameEn: 'Kuwait' },
  { code: 'QA', dial: '+974', flag: '🇶🇦', name: 'قطر', nameEn: 'Qatar' },
  { code: 'BH', dial: '+973', flag: '🇧🇭', name: 'البحرين', nameEn: 'Bahrain' },
  { code: 'OM', dial: '+968', flag: '🇴🇲', name: 'عُمان', nameEn: 'Oman' },
  { code: 'EG', dial: '+20', flag: '🇪🇬', name: 'مصر', nameEn: 'Egypt' },
  { code: 'JO', dial: '+962', flag: '🇯🇴', name: 'الأردن', nameEn: 'Jordan' },
  { code: 'LB', dial: '+961', flag: '🇱🇧', name: 'لبنان', nameEn: 'Lebanon' },
  { code: 'PS', dial: '+970', flag: '🇵🇸', name: 'فلسطين', nameEn: 'Palestine' },
  { code: 'IQ', dial: '+964', flag: '🇮🇶', name: 'العراق', nameEn: 'Iraq' },
  { code: 'SY', dial: '+963', flag: '🇸🇾', name: 'سوريا', nameEn: 'Syria' },
  { code: 'YE', dial: '+967', flag: '🇾🇪', name: 'اليمن', nameEn: 'Yemen' },
  { code: 'SD', dial: '+249', flag: '🇸🇩', name: 'السودان', nameEn: 'Sudan' },
  { code: 'LY', dial: '+218', flag: '🇱🇾', name: 'ليبيا', nameEn: 'Libya' },
  { code: 'TN', dial: '+216', flag: '🇹🇳', name: 'تونس', nameEn: 'Tunisia' },
  { code: 'DZ', dial: '+213', flag: '🇩🇿', name: 'الجزائر', nameEn: 'Algeria' },
  { code: 'MA', dial: '+212', flag: '🇲🇦', name: 'المغرب', nameEn: 'Morocco' },
  { code: 'MR', dial: '+222', flag: '🇲🇷', name: 'موريتانيا', nameEn: 'Mauritania' },
  { code: 'SO', dial: '+252', flag: '🇸🇴', name: 'الصومال', nameEn: 'Somalia' },
  { code: 'TR', dial: '+90', flag: '🇹🇷', name: 'تركيا', nameEn: 'Türkiye' },
  { code: 'GB', dial: '+44', flag: '🇬🇧', name: 'بريطانيا', nameEn: 'United Kingdom' },
  { code: 'US', dial: '+1', flag: '🇺🇸', name: 'الولايات المتحدة', nameEn: 'United States' },
  { code: 'IN', dial: '+91', flag: '🇮🇳', name: 'الهند', nameEn: 'India' },
  { code: 'PK', dial: '+92', flag: '🇵🇰', name: 'باكستان', nameEn: 'Pakistan' },
  { code: 'PH', dial: '+63', flag: '🇵🇭', name: 'الفلبين', nameEn: 'Philippines' },
];

export const DEFAULT_COUNTRY = COUNTRIES[0];

/** Separa um número completo em país + resto, para preencher o campo a partir de um valor existente */
export function splitPhone(full: string): { country: Country; number: string } {
  const digits = (full || '').replace(/\D/g, '');
  if (digits) {
    // O código mais longo que casa vence: +97 não pode roubar o lugar de +971
    const matches = COUNTRIES
      .filter(country => digits.startsWith(country.dial.slice(1)))
      .sort((a, b) => b.dial.length - a.dial.length);
    if (matches[0]) {
      return { country: matches[0], number: digits.slice(matches[0].dial.length - 1) };
    }
  }
  return { country: DEFAULT_COUNTRY, number: digits };
}

/**
 * Limpa o que a pessoa digita: no Golfo o número é escrito como 050… no dia a dia, mas com o código
 * do país o zero da frente não existe. E colar o número inteiro não pode dobrar o código.
 */
export function normalizeNationalNumber(value: string, country: Country): string {
  let digits = (value || '').replace(/\D/g, '');

  // Só tiramos o código do país quando sobra um número plausível: um número curto que por acaso
  // começa com os mesmos dígitos (9712345) continua inteiro.
  const dial = country.dial.slice(1);
  const MIN_SUBSCRIBER_DIGITS = 6;
  if (digits.startsWith(dial) && digits.length - dial.length >= MIN_SUBSCRIBER_DIGITS) {
    digits = digits.slice(dial.length);
  }

  return digits.replace(/^0+/, '');
}

/** Junta os dois campos no formato que a API espera */
export function joinPhone(country: Country, number: string): string {
  const digits = normalizeNationalNumber(number, country);
  return digits ? `${country.dial}${digits}` : '';
}

interface Props {
  country: Country;
  number: string;
  onCountryChange: (country: Country) => void;
  onNumberChange: (number: string) => void;
  language: 'ar' | 'en';
  disabled?: boolean;
  placeholder?: string;
  id?: string;
  autoFocus?: boolean;
}

export function PhoneField({
  country, number, onCountryChange, onNumberChange, language, disabled, placeholder, id, autoFocus,
}: Props) {
  const options = useMemo(
    () => COUNTRIES.map(item => ({
      ...item,
      label: `${item.flag} ${language === 'ar' ? item.name : item.nameEn} ${item.dial}`,
    })),
    [language],
  );

  return (
    <div className="flex gap-2" dir="ltr">
      <select
        value={country.code}
        onChange={event => onCountryChange(COUNTRIES.find(item => item.code === event.target.value) || DEFAULT_COUNTRY)}
        disabled={disabled}
        className="shrink-0 w-36 px-2 py-3 border border-gray-300 rounded-xl bg-white text-sm focus:outline-none focus:border-gray-500"
        aria-label={language === 'ar' ? 'مفتاح الدولة' : 'Country code'}
      >
        {options.map(item => (
          <option key={item.code} value={item.code}>
            {item.flag} {item.dial}
          </option>
        ))}
      </select>

      <input
        id={id}
        type="tel"
        inputMode="numeric"
        autoFocus={autoFocus}
        value={number}
        // O valor mostrado já é o que será enviado: sem espaços, sem zero na frente, sem código repetido
        onChange={event => onNumberChange(normalizeNationalNumber(event.target.value, country))}
        placeholder={placeholder || '501234567'}
        disabled={disabled}
        className="flex-1 min-w-0 px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-gray-500"
      />
    </div>
  );
}
